import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { loadWithExpiry, storeWithExpiry } from '../../services/Utils';
import { loadMediaItem } from './redux/media.actions';
import { APIBase, APIPost } from '../../services/API';
import { StyledImg, StyledVideo, StyledImgBackground } from '../../styles/StyledHTML';
import { SmartContainer } from '../../styles/Containers';
import { config } from '../../config';
import placeholderImage from '../../assets/placeholder-square.jpg';
import { Theme } from '../../styles/Theme';

const StyledLoading = styled(SmartContainer)`
    p {
        color: ${Theme.colors.baseText} !important;
        background-color: transparent !important;
    }

`

export const ImageCroppingPresets = {
    thumbnail: {
        process: 'thumbnail',
        w: 400, // Width
        h: 400, // Height
        q: 90, // Quality
        f: 'original', // Format
        u: false, // withoutEnlargement
        c: false // Crop
    },
    mediumpreview: {
        process: 'mediumpreview',
        w: 800, // Width
        q: 90, // Quality
        f: 'original', // Format
        u: true, // withoutEnlargement
        // c: false // Crop
    },
    preview: {
        process: 'preview',
        w: 1500, // Width
        q: 90, // Quality
        f: 'original', // Format
        u: true, // withoutEnlargement
        // c: false // Crop
    },
    previewwebp: {
        process: 'previewwebp',
        w: 1500, // Width
        q: 90, // Quality
        f: 'webp', // Format
        u: true, // withoutEnlargement
        // c: false // Crop
    },
    largepreview: {
        process: 'largepreview',
        w: 2200, // Width
        q: 90, // Quality
        f: 'original', // Format
        u: true, // withoutEnlargement
        // c: false // Crop
    },
    // TODO: HighRes (but not full res)

}



export const mediaPath = (file, storage = 's3') => {
    if (!file) return;
    if (file.indexOf('http://') > -1) return file;
    if (file.indexOf('https://') > -1) return file;

    let domain = process.env.REACT_APP_MEDIA_DOMAIN;
    if (storage === 'wasabi') domain = process.env.REACT_APP_WASABI_DOMAIN;

    return `${domain}/${file}`;
}

export const importHostedMedia = async ({ name, type, url, network = false }) => {

    try {

        // Path
        const route = (network === true) ? 'network/media' : 'media';
        const path = `${APIBase}/v1/${route}/import`;

        // Search
        const res = await APIPost(path, { url, name, type });
        const json = await res.json();
        return json;

    } catch (error) {
        return error;
    }
}

export const convertBytesToPrettySize = (bytes) => {
    var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes == 0) return '0 Byte';
    var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
}

export const useMediaAsset = ({ id, network, args = {}, noCache = false }) => {

    const [activeMedia, setActiveMedia] = useState(null);
    const [path, setPath] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mediaId, setMediaId] = useState(null);
    const [storage, setStorage] = useState(null);

    const localStorageKey = 'mediaassets';
    const expireMinutes = config.imageCacheLength;
    if (!args) args = {}
    args.expire = expireMinutes;

    const loadMedia = async (id) => {

        if (!id) return;
        let processed_id = `${id}`;
        if (args?.process) processed_id = `${id}_${args.process}`;
        setLoading(true);

        // Load From Storage First
        let localLibrary = await loadWithExpiry(localStorageKey);
        if (!localLibrary) localLibrary = {}
        else if (localLibrary[processed_id]) {
            if (noCache !== true) {
                setPath(localLibrary[processed_id]);
                setLoading(false);
                return;
            }
        }

        // Load From Server
        const res = await loadMediaItem(id, network, args);
        if (res.status === 'success') {
            setStorage(res.data?.meta?.storage)
            let process_path = res.data?.meta?.path;
            if (args?.process && res.data?.meta?.crops && res.data?.meta?.crops[args.process]) process_path = mediaPath(res.data?.meta?.crops[args.process]?.path, res.data?.meta?.storage);


            setPath(process_path);
            setActiveMedia(res.data);
            setLoading(false);

            // Regrab storage. things probably came in during that load
            localLibrary = await loadWithExpiry(localStorageKey);
            if (!localLibrary) localLibrary = {}

            // Save to Storage
            localLibrary[processed_id] = mediaPath(process_path, res.data?.meta?.storage);
            const l = storeWithExpiry(localStorageKey, localLibrary, expireMinutes - 1000);

        } else {
            // TODO: Error
            setLoading(false);
        }
    }


    // Effects
    useEffect(() => {
        // Load Media from Server
        if (activeMedia && activeMedia.id === id) {
        } else {
            loadMedia(id)
        }
    }, [id]);
    useEffect(() => {
        // Load Media from Server
        if (activeMedia && activeMedia.id === mediaId) {
        } else {
            loadMedia(mediaId)
        }
    }, [mediaId]);

    const truePath = (loading) ? null : path;

    return {
        assetLoading: loading,
        assetPath: truePath,
        assetSrc: (truePath) ? mediaPath(truePath, storage) : null,
        activeMedia,
        setMediaId
    }
}

/**
 * Checks if an image format allows transparency based on the provided file path.
 *
 * @param {string} [src=''] - The file path or URL of the image.
 * @returns {boolean} - `true` if the image format allows transparency, `false` otherwise.
 */
export const imageFormatAllowsTransparency = (src = '') => {
    const fileExtension = src.split('.').pop().toLowerCase();

    return ['png', 'gif', 'webp', 'svg'].includes(fileExtension)
}

/**
 * Checks if an HTML Image Element has transparency.
 *
 * @param {HTMLImageElement} img - The HTML Image Element to check for transparency.
 * @returns {boolean} - `true` if the image has transparency; `false` if the image is fully opaque
 */
export const mediaHasTransparency = (img) => {
    if (!img) return false;

    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set the canvas size to match the image's dimensions
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw the image on the canvas
    ctx.drawImage(img, 0, 0);

    // Get the image's pixel data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixelData = imageData.data;

    // Check for transparency in the pixel data
    for (let i = 3; i < pixelData.length; i += 4) {
        // The alpha channel (4th element) should be less than 255 (fully opaque) to have transparency
        if (pixelData[i] < 255) {
            return true; // Image has transparency
        }
    }
    return false; // Image is fully opaque
}

export const MediaImage = ({ image, id, crop = {}, network = false, ...rest }) => {

    const [loaded, setLoaded] = useState(false);
    const [transparent, setTransparent] = useState(false);
    // Capture Collab
    const thisId = (image?.id) ? image.id : id;
    // END Capture Collab
    const { assetLoading, assetPath, assetSrc, setMediaId } = useMediaAsset({ id: thisId, network, args: crop });

    useEffect(() => {

        if (image?.id) {
            setMediaId(image?.id);
        }
    }, [image])

    if (assetLoading) return (
        <StyledLoading width="100%" skeleton>
            <StyledImg radius src={placeholderImage} />
            {/* <StyledP>Hang Tight. We're loading the preview.</StyledP> */}
        </StyledLoading>
    );

    const onImageLoad = e => {
        if (rest.onLoad) {
            rest.onLoad(e);
        }

        // Only run check if media might have transparency
        // if (imageFormatAllowsTransparency(e.target.src)) {
        //     // Paul: Removing for safari issue.
        //     // const hasTransparency = mediaHasTransparency(e.target);
        //     // setTransparent(hasTransparency)
        //     // <StyledImgBackground loaded={loaded} style={{backgroundImage: `url(${assetPath})`}} />
        //     // Paul: Adding to just change bg.
        //     setTransparent(true); // Hard Coded
        // }
        setLoaded(true);
    }

    return (
        <>
            {rest.backgroundEffect === 'reelblur' && (
                <StyledImgBackground loaded={loaded} />
            )}
            <StyledImg data-media={id} {...rest} onLoad={onImageLoad} src={assetPath} />
        </>
    )
}

export const MediaVideo = ({ video, id, crop = {}, network = false, ...rest }) => {
    // Capture Collab
    const thisId = (video?.id) ? video.id : id;
    // END Capture Collab
    const { assetLoading, assetPath, assetSrc } = useMediaAsset({ id: thisId, network, args: crop });

    if (assetLoading) return (
        <StyledLoading width="100%" skeleton>
            <StyledImg radius src={placeholderImage} />
        </StyledLoading>
    );

    return (
        <>
            <StyledImgBackground loaded />
            <StyledVideo   {...rest}>
                <source src={`${assetPath}#t=0.001`} />
            </StyledVideo>
        </>
    )
}